<template>
  <div class="redacteur-ui agent-root">
    <!-- ══════════ Liste des templates (style WorkspaceManageModal) ══════════ -->
    <div v-show="!editorOpen" class="explorer-overlay" @click.self="$emit('close')">
      <div class="explorer-shell tpl-manager-shell" role="dialog" aria-modal="true" aria-label="Mes modèles">
        <button class="close-icon" @click="$emit('close')" title="Fermer" aria-label="Fermer">×</button>

        <header class="exp-header">
          <h2 class="exp-title">Mes modèles</h2>
          <button class="tpl-new-btn" @click="triggerFilePick" :disabled="isImportBusy">
            {{ isImportBusy ? 'Import…' : '+ Nouveau modèle' }}
          </button>
          <input
            ref="fileInput"
            type="file"
            accept=".docx"
            style="display:none"
            @change="onFilePicked"
          />
        </header>

        <div class="tpl-finder">
          <div v-if="isLoading" class="state-msg">Chargement…</div>

          <div v-else-if="!templates.length" class="empty-state">
            <h3>Aucun modèle pour le moment</h3>
            <p>Importez un document Word (.docx) via « + Nouveau modèle » : il devient un modèle réutilisable pour vos rédactions.</p>
          </div>

          <div v-else class="tpl-rows">
            <div v-for="t in templates" :key="t.id" class="tpl-row">
              <div class="tpl-row-main">
                <span class="tpl-row-name" :title="t.name">{{ t.name }}</span>
                <span class="tpl-row-meta">{{ (t.structure || []).length }} blocs</span>
              </div>
              <div class="tpl-row-actions">
                <button
                  v-if="t.has_template_file"
                  class="tpl-icon-btn"
                  title="Aperçu"
                  @click="previewTemplate(t.id)"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><circle cx="8" cy="8" r="2" stroke="currentColor" stroke-width="1.5"/></svg>
                </button>
                <span
                  class="tpl-use-wrap"
                  :title="t.skill ? '' : 'Template créé avant les instructions automatiques : recréez-le pour pouvoir l\'utiliser'"
                >
                  <button
                    class="tpl-use-pill"
                    :disabled="!t.skill"
                    @click="useTemplate(t)"
                  >Utiliser ce modèle</button>
                </span>
              </div>
            </div>
          </div>

          <p v-if="errorMessage" class="error-msg">{{ errorMessage }}</p>
        </div>
      </div>
    </div>

    <!-- ══════════════ Preview Modal ══════════════ -->
    <div v-if="showPreviewModal" class="modal-overlay tpl-preview-overlay" @click.self="showPreviewModal = false">
      <div class="modal-panel modal-panel-wide">
        <div class="modal-header">
          <h3>Aperçu du template</h3>
          <button class="icon-btn" @click="showPreviewModal = false">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="isLoadingPreview" class="preview-loading">Chargement de l'aperçu…</div>
          <template v-else-if="previewDocHtml">
            <div class="zone-legend">
              <span class="legend-item"><span class="legend-swatch swatch-variable"></span>Variable (rempli par l'agent)</span>
              <span class="legend-item"><span class="legend-swatch swatch-fixed"></span>Fixe</span>
              <span class="legend-item"><span class="legend-swatch swatch-optional"></span>Optionnel</span>
            </div>
            <div class="doc-page">
              <div v-if="chromeHtml(previewChrome, 'header')" class="doc-chrome doc-chrome-header"
                   v-html="chromeHtml(previewChrome, 'header')"></div>
              <div v-html="previewDocHtml"></div>
              <div v-if="chromeHtml(previewChrome, 'footer')" class="doc-chrome doc-chrome-footer"
                   v-html="chromeHtml(previewChrome, 'footer')"></div>
            </div>
          </template>
          <div v-else class="empty-hint-sm">Aperçu non disponible</div>
        </div>
      </div>
    </div>

    <!-- ══════════════ Éditeur (création d'un nouveau modèle) ══════════════ -->
    <TemplateEditorModal
      :visible="editorOpen"
      :file="editorFile"
      @close="onEditorClose"
      @saved="onEditorSaved"
    />
  </div>
</template>

<script>
// Gestion des templates du Rédacteur : liste (nom, aperçu, « Utiliser ce
// modèle »), création via TemplateEditorModal. Style calqué sur
// WorkspaceManageModal. Événements : close, use ({templateId, templateName,
// skillId}), saved (template créé, avec son skill).
import TemplateEditorModal from './TemplateEditorModal.vue';
import { apiFetch, chromeHtml, buildDocHtml } from './shared';

export default {
  name: 'TemplateManagerModal',
  components: { TemplateEditorModal },
  data() {
    return {
      templates: [],
      isLoading: true,
      errorMessage: '',

      // Création (éditeur plein écran)
      editorOpen: false,
      editorFile: null,

      // Aperçu
      showPreviewModal: false,
      previewElements: null,
      previewStructure: null,
      previewChrome: null,
      isLoadingPreview: false,
    };
  },

  computed: {
    isImportBusy() {
      return this.editorOpen;
    },
    previewDocHtml() {
      if (!this.previewElements) return '';
      return buildDocHtml(this.previewElements, this.previewStructure || [], null, true);
    },
  },

  async mounted() {
    await this.loadTemplates();
  },

  methods: {
    chromeHtml,

    async loadTemplates() {
      this.errorMessage = '';
      try {
        const data = await apiFetch('/redacteur/api/v1/templates');
        this.templates = data.templates || data || [];
      } catch (e) {
        console.error('loadTemplates', e);
        this.errorMessage = 'Impossible de charger les modèles : ' + e.message;
      } finally {
        this.isLoading = false;
      }
    },

    // ── Création d'un nouveau modèle ────────────────
    triggerFilePick() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },

    onFilePicked(event) {
      const file = event.target.files && event.target.files[0];
      // reset pour permettre de re-sélectionner le même fichier (ex. après
      // un refus 413/415 puis correction du document sous le même nom)
      event.target.value = '';
      if (!file) return;
      this.editorFile = file;
      this.editorOpen = true;
    },

    onEditorClose() {
      this.editorOpen = false;
      this.editorFile = null;
    },

    onEditorSaved(saved) {
      this.editorOpen = false;
      this.editorFile = null;
      // Émettre d'abord : sur /redacteur le parent ferme le modal aussitôt
      // (comportement d'avant l'extraction), le rechargement sert au cas
      // où le gestionnaire reste ouvert (dashboard).
      this.$emit('saved', saved);
      this.loadTemplates();
    },

    // ── Utilisation d'un template ───────────────────
    useTemplate(t) {
      if (!t.skill) return;
      this.$emit('use', {
        templateId: t.id,
        templateName: t.name,
        skillId: t.skill.id,
      });
    },

    // ── Aperçu ──────────────────────────────────────
    async previewTemplate(templateId) {
      this.showPreviewModal = true;
      this.isLoadingPreview = true;
      this.previewElements = null;
      this.previewStructure = null;
      try {
        const data = await apiFetch(`/redacteur/api/v1/templates/${templateId}/preview`);
        this.previewElements = data.elements || null;
        this.previewStructure = data.structure || [];
        this.previewChrome = data.chrome || null;
      } catch (e) {
        alert('Erreur : ' + e.message);
      } finally {
        this.isLoadingPreview = false;
      }
    },
  },
};
</script>

<style src="./redacteur-ui.css"></style>

<style scoped>
/* ───────────── Shell (même langage que WorkspaceManageModal) ───────────── */
.explorer-overlay {
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex; justify-content: center; align-items: center;
  z-index: 1000;
  padding: 24px;
  box-sizing: border-box;
}
.explorer-shell {
  position: relative;
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 720px;
  max-height: 92vh;
  overflow-y: auto;
  padding: 32px 36px;
  box-sizing: border-box;
  box-shadow: 0 20px 60px rgba(0,0,0,0.35);
}
.close-icon {
  position: absolute;
  top: 14px; right: 18px;
  background: transparent; border: none;
  font-size: 1.9rem; line-height: 1; color: #9ca3af;
  cursor: pointer; padding: 2px 8px;
  z-index: 5;
}
.close-icon:hover { color: #374151; }

/* ───────────── Header : titre + bouton de création ───────────── */
.exp-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 18px;
  margin-bottom: 16px;
  padding-right: 28px; /* laisse la place au bouton fermer */
  flex-wrap: wrap;
}
.exp-title {
  margin: 0;
  font-family: 'Poppins', ui-sans-serif, system-ui, sans-serif;
  font-size: 1.3rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}
.tpl-new-btn {
  margin-left: auto;
  padding: 7px 16px;
  background: #fff;
  color: #6c63ff;
  border: 1px solid rgba(108, 99, 255, 0.5);
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.tpl-new-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #6c63ff, #5a52d5);
  border-color: transparent;
  color: #fff;
}
.tpl-new-btn:disabled { opacity: 0.55; cursor: not-allowed; }

/* ───────────── Liste (lignes) ───────────── */
.tpl-finder {
  background: linear-gradient(to right, #E9EFF4, #E2FAFFCC);
  border-radius: 12px;
  padding: 18px;
  min-height: 220px;
  box-shadow:
    inset 0 1px 3px rgba(0, 0, 0, 0.04),
    0 1px 2px rgba(0, 0, 0, 0.03);
}
.tpl-rows {
  background: #fff;
  border: 1px solid #e0e4e8;
  border-radius: 12px;
  padding: 4px 15px;
}
.tpl-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f1f4;
}
.tpl-row:last-child { border-bottom: none; }
.tpl-row-main {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
}
.tpl-row-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tpl-row-meta {
  font-size: 0.72rem;
  color: #9ca3af;
  flex-shrink: 0;
}
.tpl-row-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.tpl-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.tpl-icon-btn:hover { background: #f5f3ff; color: #6c63ff; }

/* « Utiliser ce modèle » : pill violette primaire (cf. ws-scope-pill) */
.tpl-use-wrap { display: inline-block; }
.tpl-use-pill {
  display: block;
  padding: 6px 14px;
  text-align: center;
  background: #fff;
  color: #6c63ff;
  border: 1px solid rgba(108, 99, 255, 0.5);
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.tpl-use-pill:hover:not(:disabled) {
  background: linear-gradient(135deg, #6c63ff, #5a52d5);
  border-color: transparent;
  color: #fff;
}
.tpl-use-pill:disabled { opacity: 0.45; cursor: not-allowed; }

/* ───────────── États ───────────── */
.state-msg {
  padding: 40px 0; text-align: center; color: #6b7280;
}
.empty-state {
  text-align: center; padding: 60px 20px;
}
.empty-state h3 { margin: 0 0 8px; color: #111827; font-size: 1.1rem; }
.empty-state p { margin: 0; color: #6b7280; font-size: 0.9rem; }
.error-msg { color: #b91c1c; font-size: 0.85rem; margin: 12px 2px 0; }
</style>
